// @ts-nocheck
/**
 * レベル計算システム
 *
 * 責務:
 * - 難易度判定（maxFloorベース）
 * - 敵レベル計算
 * - 敵種別補正適用
 * - レベルキャップ適用
 */

/**
 * 難易度設定
 */
const DIFFICULTY_CONFIG = {
  beginner: {
    name: '初級',
    maxFloorRange: [10, 20],
    coefficient: 1.0,
    levelCap: 30,
    bossRarities: ['R'],
  },
  intermediate: {
    name: '中級',
    maxFloorRange: [21, 50],
    coefficient: 1.2,
    levelCap: 60,
    bossRarities: ['R', 'SR'],
  },
  advanced: {
    name: '上級',
    maxFloorRange: [51, 100],
    coefficient: 1.5,
    levelCap: 100,
    bossRarities: ['SR', 'UR'],
  },
  expert: {
    name: '超級',
    maxFloorRange: [101, Infinity],
    coefficient: 2.0,
    levelCap: 150,
    bossRarities: ['UR', 'HR'],
  },
};

/**
 * 敵種別によるレベル補正
 */
const ENEMY_TYPE_MODIFIERS = {
  normal: 0.8,    // 通常敵
  strong: 1.0,    // 強敵
  boss: 1.5,      // ボス
  minion: 0.7,    // 取り巻き
};

/**
 * 敵種別によるレアリティ
 */
const ENEMY_TYPE_RARITIES = {
  normal: 'R',
  strong: 'SR',
  minion: 'UC',
};

/**
 * レベル計算クラス
 */
class LevelCalculator {
  /**
   * @param {number} maxFloor - ダンジョンの最大フロア数
   * @param {number} [seed] - シード値（ボスレアリティ決定用）
   */
  constructor(maxFloor, seed = 0) {
    this.maxFloor = maxFloor;
    this.seed = seed;
    this.difficulty = this._determineDifficulty(maxFloor);
  }

  /**
   * 難易度を判定
   *
   * @param {number} maxFloor - 最大フロア数
   * @returns {Object} 難易度設定
   * @private
   */
  _determineDifficulty(maxFloor) {
    for (const [key, config] of Object.entries(DIFFICULTY_CONFIG)) {
      const [min, max] = config.maxFloorRange;
      if (maxFloor >= min && maxFloor <= max) {
        return { key, ...config };
      }
    }
    return { key: 'expert', ...DIFFICULTY_CONFIG.expert };
  }

  /**
   * 難易度情報を取得
   *
   * @returns {Object} 難易度情報
   */
  getDifficulty() {
    return this.difficulty;
  }

  /**
   * 難易度係数を取得
   *
   * @returns {number} 難易度係数
   */
  getCoefficient() {
    return this.difficulty.coefficient;
  }

  /**
   * レベルキャップを取得
   *
   * @returns {number} レベルキャップ
   */
  getLevelCap() {
    return this.difficulty.levelCap;
  }

  /**
   * 敵レベルを計算
   *
   * @param {number} floorNumber - 現在のフロア番号
   * @param {string} enemyType - 敵種別 ('normal' | 'strong' | 'boss' | 'minion')
   * @returns {number} 計算されたレベル
   */
  calculateLevel(floorNumber, enemyType = 'normal') {
    // 基本レベル = フロア番号 × 難易度係数
    const baseLevel = floorNumber * this.difficulty.coefficient;

    // 敵種別補正
    const typeModifier = ENEMY_TYPE_MODIFIERS[enemyType] || 1.0;

    // 最終レベル（キャップ適用）
    const calculatedLevel = Math.floor(baseLevel * typeModifier);
    const cappedLevel = Math.min(calculatedLevel, this.difficulty.levelCap);

    // 最低レベルは1
    return Math.max(1, cappedLevel);
  }

  /**
   * ボスレアリティを決定（seed依存）
   *
   * @param {number} [floorNumber] - フロア番号（将来的な階層別レアリティ用）
   * @returns {string} レアリティ ('R' | 'SR' | 'UR' | 'HR')
   */
  determineBossRarity(floorNumber = this.maxFloor) {
    const rarities = this.difficulty.bossRarities;

    if (rarities.length === 1) {
      return rarities[0];
    }

    // seedを使ってレアリティを決定
    const index = this.seed % rarities.length;
    return rarities[index];
  }

  /**
   * 敵種別のレアリティを取得
   *
   * @param {string} enemyType - 敵種別
   * @returns {string} レアリティ
   */
  getEnemyTypeRarity(enemyType) {
    if (enemyType === 'boss') {
      return this.determineBossRarity();
    }
    return ENEMY_TYPE_RARITIES[enemyType] || 'R';
  }

  /**
   * フロア進行率を計算
   *
   * @param {number} floorNumber - 現在のフロア番号
   * @returns {number} 進行率 (0-1)
   */
  getProgressRatio(floorNumber) {
    return Math.min(1, floorNumber / this.maxFloor);
  }

  /**
   * 取り巻きの数を決定
   *
   * @param {number} floorNumber - フロア番号
   * @returns {number} 取り巻き数 (0-2)
   */
  determineMinionCount(floorNumber) {
    const progress = this.getProgressRatio(floorNumber);

    // 進行度が高いほど取り巻きが増える
    if (progress < 0.3) return 0;
    if (progress < 0.6) return this.seed % 2;
    return 1 + (this.seed % 2);
  }
}

export default LevelCalculator;
export {
  LevelCalculator,
  DIFFICULTY_CONFIG,
  ENEMY_TYPE_MODIFIERS,
  ENEMY_TYPE_RARITIES,
};
